import {
  faComment,
  faDownload,
  faEarDeaf,
  faEarListen,
  faFlask,
  faGear,
  faRightFromBracket,
  faTextHeight,
  faVolumeHigh,
  faVolumeXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useCookies } from 'react-cookie';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authService, useSessionId } from '@/core/auth/export';
import { analyticsService, AnalyticsEvent } from '@/shared/analytics/export';
import { installService } from '@/shared/install/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import {
  Permissions,
  useEffectivePermissions,
  useToggleFeaturesMenuItems,
} from '@/core/permissions/export';
import { MenuListItem } from '@/shared/components/export';
import { readerService } from '@/modules/calendar/export';
import TextSizeMenuContent from '@/app/dashboard/components/text-size-menu-content';

const defaultReaderSections = [
  { id: 'oil-bullets', name: 'Oil', enabled: false },
  { id: 'gas-bullets', name: 'Gas', enabled: false },
  { id: 'fx-bullets', name: 'FX Bullets', enabled: false },
  { id: 'fi-bullets', name: 'FI Bullets', enabled: false },
  { id: 'eu-credit-bullets', name: 'Credit', enabled: false },
];

interface ReaderSection {
  id: string;
  name: string;
  enabled: boolean;
}

/** The user-profile action list shared by the desktop dropdown (DashboardUserMenu) and the mobile full-page hamburger menu. */
export function useUserMenuItems({
  notificationSoundOn,
  setNotificationSoundOn,
}: {
  notificationSoundOn: boolean;
  setNotificationSoundOn: (on: boolean) => void;
}) {
  const auth0 = useAuth0();

  const sessionId = useSessionId();
  const subject = authService.getSubjectData();
  // eslint-disable-next-line
  const [_, _2, deleteCookie] = useCookies();
  const [showChatKeysModal, setShowChatKeysModal] = useState(false);
  const [query, setQuery] = useSearchParams();
  const openWidget = useOpenWidget();
  const permissions = useEffectivePermissions();
  const toggleFeaturesItems = useToggleFeaturesMenuItems();

  const [readerSections, setReaderSections] = useState<ReaderSection[]>(defaultReaderSections);
  const [readerEnabled, setReaderEnabled] = useState(false);

  useEffect(() => {
    const savedSections = readerService.getReaderSections();
    setReaderSections(
      defaultReaderSections.map(section => ({
        ...section,
        enabled: savedSections.includes(section.id),
      })),
    );
    setReaderEnabled(savedSections.length > 0);
  }, []);

  const toggleReaderSection = (sectionId: string) => {
    const updatedSections = readerSections.map(section =>
      section.id === sectionId ? { ...section, enabled: !section.enabled } : section,
    );
    setReaderSections(updatedSections);
    readerService.setReaderSections(updatedSections.filter(s => s.enabled).map(s => s.id));
  };

  const toggleReaderEnabled = () => {
    const nextEnabled = !readerEnabled;
    setReaderEnabled(nextEnabled);
    readerService.setReaderSections(
      nextEnabled ? readerSections.filter(s => s.enabled).map(s => s.id) : [],
    );
  };

  const logout = () => {
    const source = authService.getCredentials()?.token.source;

    authService.logout().then(value => {
      if (source === 'auth0') {
        //remove shared cookies with website
        deleteCookie('permissions', {
          path: '/',
          domain: '.mnimarkets.com',
        });
        deleteCookie('refresh', {
          path: '/',
          domain: '.mnimarkets.com',
        });
        deleteCookie('token', {
          path: '/',
          domain: '.mnimarkets.com',
        });
        deleteCookie('kmsi_exp', {
          path: '/',
          domain: '.mnimarkets.com',
        });
        console.warn('Logging out of auth0');

        analyticsService.recordAnalyticsEvent(AnalyticsEvent.LOGOUT, {
          subject: subject,
          session: {
            id: sessionId,
          },
        });

        auth0.logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      } else {
        window.location.reload();
      }
    });
  };

  useEffect(() => {
    const openChatKeysModal = query.get('chat-keys');
    if (openChatKeysModal) {
      setShowChatKeysModal(true);
      query.delete('chat-keys');
      setQuery(query);
    }
    // eslint-disable-next-line
  }, []);

  const hasChatPermissions = permissions.some(p => p.startsWith('read:chat-feed'));
  const hasAlphaAccess = permissions.includes(Permissions.ALPHA_ACCESS);

  const items: MenuListItem[] = [
    {
      key: 'settings',
      label: 'Settings',
      icon: faGear,
      onSelect: () => openWidget('settings'),
    },
    {
      key: 'mute',
      label: 'Notification sound',
      icon: notificationSoundOn ? faVolumeHigh : faVolumeXmark,
      selected: notificationSoundOn,
      onSelect: () => setNotificationSoundOn(!notificationSoundOn),
    },
    ...(hasAlphaAccess
      ? [
          {
            key: 'reader',
            label: 'Reader settings',
            icon: readerEnabled ? faEarListen : faEarDeaf,
            page: [
              {
                key: 'reader-enabled',
                label: 'Enable Reader',
                selected: readerEnabled,
                onSelect: toggleReaderEnabled,
              },
              { key: 'reader-divider', label: '', divider: true },
              ...readerSections.map(section => ({
                key: `reader-${section.id}`,
                label: section.name,
                selected: section.enabled && readerEnabled,
                disabled: !readerEnabled,
                onSelect: () => toggleReaderSection(section.id),
              })),
            ],
          },
          {
            key: 'text-size',
            label: 'Text size',
            icon: faTextHeight,
            content: <TextSizeMenuContent />,
          },
        ]
      : []),
    ...(toggleFeaturesItems
      ? [
          {
            key: 'toggle-features',
            label: 'Toggle features',
            icon: faFlask,
            page: toggleFeaturesItems,
          },
        ]
      : []),
    ...(hasChatPermissions
      ? [
          {
            key: 'chat',
            label: 'Chat activation',
            icon: faComment,
            onSelect: () => setShowChatKeysModal(true),
          },
        ]
      : []),
    ...(installService.canInstall()
      ? [
          {
            key: 'install',
            label: 'Install',
            icon: faDownload,
            onSelect: () => installService.install(),
          },
        ]
      : []),
    { key: 'logout-divider', label: '', divider: true },
    { key: 'logout', label: 'Logout', icon: faRightFromBracket, onSelect: logout },
  ];

  return { items, showChatKeysModal, setShowChatKeysModal };
}
